import { z } from 'zod';
import { AGENT_DESCRIPTION_MAX, AGENT_NAME_MAX, AGENT_SYSTEM_PROMPT_MAX, CALLBACK_TOKEN_MIN, LLM_DEFAULT_TEMPERATURE, LLM_DEFAULT_TOKENS, LLM_MAX_TEMPERATURE, LLM_MAX_TOKENS, LLM_MIN_TEMPERATURE, LLM_MIN_TOKENS, UPLOAD_DEFAULT_SIZE_MB, UPLOAD_MAX_SIZE_MB, UPLOAD_MIN_SIZE_MB, } from '../constants/limits.js';
import { AGENT_PROFILE_PROVIDERS, getDefaultModelForProvider } from '../types/agent-profile.js';
/** Hex color #rgb or #rrggbb. */
const HexColor = z.string().regex(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i, 'Must be a hex color');
/** url-safe slug. */
const SlugSchema = z
    .string()
    .min(1)
    .max(80)
    .regex(/^[a-z0-9-]+$/, 'Lowercase letters, numbers, and hyphens only');
/** JSON Schema is just an object — deep validation is done server-side via AJV. */
const JsonSchemaObject = z.record(z.string(), z.unknown());
const DispatchMode = z.enum([
    'chat_only',
    'n8n_webhook',
    'direct_provider',
    'video_editor',
    'content_calendar',
    'media_calendar',
]);
/**
 * Base shape — used for create and partial-update.
 *
 * The webhook + schema requirements depend on `dispatchMode`; the cross-field
 * guards below enforce that on top-level Create/Update. We default
 * `dispatchMode` to `n8n_webhook` so the historical create-agent body shape
 * (mandatory webhook URL + JSON schema) still parses unchanged.
 */
const AgentBase = z.object({
    name: z.string().min(1).max(AGENT_NAME_MAX),
    slug: SlugSchema,
    description: z.string().max(AGENT_DESCRIPTION_MAX).optional(),
    avatarUrl: z.string().url().optional(),
    accentColor: HexColor.optional(),
    systemPrompt: z.string().min(1).max(AGENT_SYSTEM_PROMPT_MAX),
    outputJsonSchema: JsonSchemaObject.optional(),
    outputExample: JsonSchemaObject.optional(),
    /**
     * LLM provider for the chat reply. Reuses the AGENT_PROFILE_PROVIDERS
     * enum so chat agents and AgentProfile both pull from the same universe
     * + the same Settings → LLM API key store. Defaults to 'openai' for
     * backward compat with pre-v2.0.0 rows.
     */
    provider: z.enum(AGENT_PROFILE_PROVIDERS).default('openai'),
    /**
     * Model name. Any non-empty id up to 120 chars — the live provider list
     * decides what actually runs. Default is the OpenAI fallback catalog's
     * first id when the client omits the field.
     */
    model: z.string().trim().min(1).max(120).default(getDefaultModelForProvider('openai')),
    temperature: z
        .number()
        .min(LLM_MIN_TEMPERATURE)
        .max(LLM_MAX_TEMPERATURE)
        .default(LLM_DEFAULT_TEMPERATURE),
    maxTokens: z
        .number()
        .int()
        .min(LLM_MIN_TOKENS)
        .max(LLM_MAX_TOKENS)
        .default(LLM_DEFAULT_TOKENS),
    dispatchMode: DispatchMode.default('n8n_webhook'),
    directProviderModelId: z.string().min(1).max(200).optional(),
    videoEditorOperationId: z.string().min(1).max(50).optional(),
    /**
     * Drive share URL (or bare folder id) where direct_provider outputs are
     * archived. Server extracts the id via lib/drive-folder.ts. Required for
     * direct_provider mode (see cross-field rule below).
     */
    driveOutputFolderUrl: z.string().min(1).max(2_000).optional(),
    n8nWebhookUrl: z.string().url().optional(),
    n8nCallbackToken: z.string().min(CALLBACK_TOKEN_MIN).optional(),
    allowImageUpload: z.boolean().default(false),
    allowVideoUpload: z.boolean().default(false),
    allowVoiceInput: z.boolean().default(true),
    /**
     * UST-00008 — accept an .xlsx of content-calendar rows in chat. No cross-field
     * rule here: the server clears it for every dispatch mode except
     * content_calendar, which is stricter than a refine and can't be bypassed by
     * a later PATCH that only changes the mode.
     */
    allowSheetUpload: z.boolean().default(false),
    /**
     * Drive share URL or bare folder id. Optional — no folder means no archival.
     * An empty string is meaningful: it CLEARS a folder that was set before.
     * `.optional()` alone couldn't express "explicitly set to none" versus "left
     * untouched", and without the empty case a folder could be set but never
     * removed. Same reasoning as `driveSheetFolderUrl` on the calendar config.
     */
    driveSheetFolderUrl: z.string().max(2_000).optional(),
    mediaN8nWebhookUrl: z.string().url().optional(),
    mediaN8nCallbackToken: z.string().min(CALLBACK_TOKEN_MIN).optional(),
    /**
     * Cloudflare R2 key prefixes. Accepts any non-empty string up to 256 chars;
     * server normalizes trailing slash. An empty string is treated as "use the
     * bucket root" (key prefix = "").
     */
    r2ImageFolder: z.string().max(256).optional(),
    r2VideoFolder: z.string().max(256).optional(),
    maxUploadSizeMb: z
        .number()
        .int()
        .min(UPLOAD_MIN_SIZE_MB)
        .max(UPLOAD_MAX_SIZE_MB)
        .default(UPLOAD_DEFAULT_SIZE_MB),
});
/** Cross-field guards: media flags + dispatch-mode-specific required fields. */
function applyAgentCrossFieldRules(data, ctx) {
    // Media upload requirement check was relaxed — admins can now wire R2
    // globally in Settings → Cloudflare and pick a folder per agent (R2
    // folder fields here), without needing an n8n media webhook. The upload
    // route validates at dispatch time that AT LEAST one of R2 (with global
    // config) or n8n is reachable, so we don't reject here.
    // Note: the legacy rule that REQUIRED mediaN8nWebhookUrl + token whenever
    // image/video upload was enabled was relaxed because R2 is now the
    // preferred path (set globally in Settings → Cloudflare + per-agent folder
    // prefix). The runtime upload service picks R2 first and falls back to
    // n8n only if R2 isn't viable.
    if (data.dispatchMode === 'n8n_webhook') {
        if (!data.n8nWebhookUrl) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['n8nWebhookUrl'],
                message: 'Required for the n8n webhook dispatch mode',
            });
        }
        if (!data.n8nCallbackToken) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['n8nCallbackToken'],
                message: 'Required for the n8n webhook dispatch mode',
            });
        }
        if (!data.outputJsonSchema) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['outputJsonSchema'],
                message: 'Required for the n8n webhook dispatch mode',
            });
        }
    }
    else if (data.dispatchMode === 'direct_provider') {
        if (!data.directProviderModelId) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['directProviderModelId'],
                message: 'Pick a fal.ai/kie.ai model for the direct provider dispatch mode',
            });
        }
        if (!data.driveOutputFolderUrl) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['driveOutputFolderUrl'],
                message: 'Paste the Drive folder URL where generated output should be archived',
            });
        }
    }
    else if (data.dispatchMode === 'video_editor') {
        if (!data.videoEditorOperationId) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['videoEditorOperationId'],
                message: 'Pick a video-editor operation (trim/merge/crop/resize)',
            });
        }
        // video_editor agents always need to attach a video to chat — auto-enable
        // when not already on.
        if (!data.allowVideoUpload) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['allowVideoUpload'],
                message: 'Enable video uploads — video-editor agents need a source video from the user',
            });
        }
    }
}
export const CreateAgentRequestSchema = AgentBase.superRefine(applyAgentCrossFieldRules);
/**
 * Update accepts any subset — but if it touches any media-related field we
 * re-validate the cross-field rule against the merged record. The route layer
 * loads the current row first and merges before validating; we expose a
 * partial-shape schema here for the initial parse.
 */
export const UpdateAgentRequestSchema = AgentBase.partial().extend({
    isActive: z.boolean().optional(),
});
export const AgentIdParamSchema = z.object({ id: z.string().uuid() });
export const AgentTestRequestSchema = z.object({
    message: z.string().min(1).max(8000),
});
//# sourceMappingURL=agent.schema.js.map